import { createContext, useContext, useMemo } from 'react';
import type { AuiNode } from '../../types/canvases';

const AuiFillPathContext = createContext<ReadonlySet<string> | null>(null);

export function AuiFillPathProvider({
  fillPath,
  children,
}: {
  fillPath: ReadonlySet<string> | null;
  children: React.ReactNode;
}) {
  const value = useMemo(() => fillPath, [fillPath]);
  return (
    <AuiFillPathContext.Provider value={value}>{children}</AuiFillPathContext.Provider>
  );
}

export function useAuiFillPath(nodeId: string): boolean {
  const set = useContext(AuiFillPathContext);
  return set?.has(nodeId) ?? false;
}

/**
 * A node is fill-eligible if it would benefit from flex-filling its parent.
 * Today only MarkdownEditor without an explicit `maxHeight` qualifies.
 * Add other large-content components here as they appear.
 */
function isFillAnchor(node: AuiNode): boolean {
  if (node.component.type !== 'MarkdownEditor') return false;
  const max = node.component.props?.maxHeight;
  return max === undefined || max === null || max === '';
}

/**
 * Compute the set of node IDs on the fill path: a fill anchor plus every
 * ancestor down from the root. Fill mode only engages when the tree holds
 * exactly ONE fill anchor — that guarantees an unbroken root→anchor flex
 * chain, so bounding the surface to the panel height (h-full on the Card)
 * cannot strand content-sized siblings outside it. With two or more anchors
 * there is no single chain: the surface must stay content-sized and grow,
 * so we return an empty set (any partial marking would pin the Card's
 * height while the root column keeps its natural size and overflows).
 */
export function computeFillPath(rootId: string, nodeMap: Map<string, AuiNode>): Set<string> {
  const path = new Set<string>();
  let anchorCount = 0;
  function walk(id: string): boolean {
    const node = nodeMap.get(id);
    if (!node) return false;
    if (isFillAnchor(node)) {
      anchorCount += 1;
      path.add(id);
      return true;
    }
    const childIds = node.component.children ?? [];
    let fillCount = 0;
    for (const childId of childIds) {
      if (walk(childId)) fillCount += 1;
    }
    if (fillCount === 1) {
      path.add(id);
      return true;
    }
    return false;
  }
  const rootOnPath = walk(rootId);
  if (anchorCount !== 1 || !rootOnPath) return new Set();
  return path;
}
